import {
    DefaultButton,
    MessageBar,
    MessageBarType,
    ProgressIndicator,
    Stack,
    Text,
} from "@fluentui/react";
import {useBoolean} from "@fluentui/react-hooks";
import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {ExternalAuthProvider} from "../API";
import {AppRoute} from "../AppRoutes";
import {AuthContext} from "../contexts/AuthContext";
import {
    buildExternalAuthRedirectUri,
    exchangeExternalAuthTicket,
    externalAuthErrorMessage,
    externalProviderTitle,
    loadExternalAuthProviders,
    loadMyExternalIdentities,
    parseExternalAuthSearch,
    removeExternalAuthSearchParams,
    startExternalLink,
    unlinkExternalIdentity,
    WebExternalIdentityLink,
} from "../helpers/ExternalAuthWebHelper";
import {TitleAndContentLayout} from "./TitleAndContentLayout";

type ExternalAuthMessage = {
    type: MessageBarType;
    text: string;
};

export const UserSettingsExternalAccounts = () => {
    const {t} = useTranslation();
    const history = useNavigate();
    const location = useLocation();
    const {SetToken, UserInfo} = useContext(AuthContext);
    const [providers, setProviders] = useState<ExternalAuthProvider[]>([]);
    const [identities, setIdentities] = useState<WebExternalIdentityLink[]>([]);
    const [message, setMessage] = useState<ExternalAuthMessage>();
    const [loading, {setTrue: setLoading, setFalse: setNotLoading}] =
        useBoolean(false);
    const currentUserId = UserInfo?.user?.id;

    const callbackParams = useMemo(
        () => parseExternalAuthSearch(location.search),
        [location.search],
    );

    const clearCallbackParams = useCallback(() => {
        const nextSearch = removeExternalAuthSearchParams(location.search);
        history(`${location.pathname}${nextSearch}${location.hash}`, {
            replace: true,
        });
    }, [history, location.hash, location.pathname, location.search]);

    const loadData = useCallback(async () => {
        const [loadedProviders, loadedIdentities] = await Promise.all([
            loadExternalAuthProviders(),
            loadMyExternalIdentities(),
        ]);
        setProviders(loadedProviders);
        setIdentities(loadedIdentities);
    }, []);

    const refreshData = useCallback(async () => {
        setLoading();
        try {
            await loadData();
        } catch (error) {
            setMessage({
                type: MessageBarType.error,
                text: externalAuthErrorMessage(
                    error,
                    "Nije moguce ucitati povezane racune.",
                ),
            });
        } finally {
            setNotLoading();
        }
    }, [loadData, setLoading, setNotLoading]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    useEffect(() => {
        if (
            callbackParams.flow !== "link" ||
            !callbackParams.ticket ||
            !currentUserId
        ) {
            return;
        }

        setLoading();
        exchangeExternalAuthTicket(callbackParams.ticket, currentUserId)
            .then((result) => {
                if (result.token) SetToken(result.token);
                setMessage({
                    type: MessageBarType.success,
                    text: "Microsoft racun je povezan s Onix korisnikom.",
                });
                return loadData();
            })
            .catch((error) => {
                setMessage({
                    type: MessageBarType.warning,
                    text: externalAuthErrorMessage(
                        error,
                        "Racun je povezan, ali potvrda prijave nije dovrsena.",
                    ),
                });
                return loadData();
            })
            .finally(() => {
                clearCallbackParams();
                setNotLoading();
            });
    }, [
        callbackParams.flow,
        callbackParams.ticket,
        clearCallbackParams,
        currentUserId,
        loadData,
        setLoading,
        setNotLoading,
        SetToken,
    ]);

    const connectProvider = useCallback(
        async (provider: ExternalAuthProvider) => {
            setLoading();
            setMessage(undefined);

            try {
                const redirectUri = buildExternalAuthRedirectUri(
                    window.location.origin,
                    AppRoute.SETTINGS_EXTERNAL_ACCOUNTS,
                );
                const result = await startExternalLink(provider, redirectUri);
                window.location.assign(result.url);
            } catch (error) {
                setMessage({
                    type: MessageBarType.error,
                    text: externalAuthErrorMessage(
                        error,
                        "Vanjski racun nije moguce povezati.",
                    ),
                });
                setNotLoading();
            }
        },
        [setLoading, setNotLoading],
    );

    const disconnectIdentity = useCallback(
        async (identity: WebExternalIdentityLink) => {
            const label =
                identity.externalIdentity.verifiedEmail ||
                identity.externalIdentity.displayName ||
                externalProviderTitle(identity.externalIdentity.provider);

            if (!window.confirm(`Odspojiti ${label}?`)) return;

            setLoading();
            setMessage(undefined);

            try {
                await unlinkExternalIdentity(identity.externalIdentityId);
                setMessage({
                    type: MessageBarType.success,
                    text: "Vanjski racun je odspojen.",
                });
                await loadData();
            } catch (error) {
                setMessage({
                    type: MessageBarType.error,
                    text: externalAuthErrorMessage(
                        error,
                        "Vanjski racun nije moguce odspojiti.",
                    ),
                });
            } finally {
                setNotLoading();
            }
        },
        [loadData, setLoading, setNotLoading],
    );

    return (
        <TitleAndContentLayout title={t("external-accounts")} lessPadding isSubtitle pad>
            <Stack tokens={{childrenGap: 16}} styles={{root: {maxWidth: 560}}}>
                {message ? (
                    <MessageBar messageBarType={message.type}>{message.text}</MessageBar>
                ) : null}

                <Stack tokens={{childrenGap: 8}}>
                    <Text variant="large">Povezani racuni</Text>
                    {identities.length === 0 ? (
                        <Text>Nema povezanih vanjskih racuna.</Text>
                    ) : (
                        identities.map((identity) => (
                            <Stack
                                horizontal
                                horizontalAlign="space-between"
                                verticalAlign="center"
                                tokens={{childrenGap: 16}}
                                key={identity.id}
                                styles={{
                                    root: {
                                        borderBottom: "1px solid #edebe9",
                                        paddingBottom: 8,
                                        paddingTop: 8,
                                    },
                                }}
                            >
                                <Stack tokens={{childrenGap: 2}}>
                                    <Text>
                                        {externalProviderTitle(
                                            identity.externalIdentity.provider,
                                        )}
                                    </Text>
                                    <Text variant="small">
                                        {identity.externalIdentity.verifiedEmail ||
                                            identity.externalIdentity.displayName ||
                                            "Povezan vanjski racun"}
                                    </Text>
                                </Stack>
                                <DefaultButton
                                    type="button"
                                    text="Odspoji"
                                    disabled={loading}
                                    onClick={() => disconnectIdentity(identity)}
                                />
                            </Stack>
                        ))
                    )}
                </Stack>

                <Stack tokens={{childrenGap: 8}}>
                    <Text variant="large">Dodaj prijavu</Text>
                    {providers.length === 0 ? (
                        <Text>Vanjske prijave trenutno nisu konfigurirane.</Text>
                    ) : (
                        providers.map((provider) => (
                            <DefaultButton
                                key={provider}
                                type="button"
                                text={`Povezi ${externalProviderTitle(provider)} racun`}
                                disabled={loading}
                                onClick={() => connectProvider(provider)}
                            />
                        ))
                    )}
                </Stack>

                {loading ? <ProgressIndicator/> : null}
            </Stack>
        </TitleAndContentLayout>
    );
};
